'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertCircle, Link2, Search } from 'lucide-react'
import { BaseDashboard, DashboardSection, baseDashboardClasses } from '../BaseDashboard'
import { ResizableTable, TableTd, type ColumnDef } from '../ResizableTable'
import { useStickyStackTop } from '../stickyStack'
import { useListReturn, LAST_OPENED_ROW_CLASS } from './useListReturn'
import { useDetailPath } from './useDetailPath'
import { StatTile } from '../charts/StatTile'
import { RegistrationWaveCard, QualityDistributionCard, ScreenFunnelCard } from './DomainCandidateCharts'
import { BrandJobsCard } from './BrandJobsCard'
import { CollapsibleSection } from './CollapsibleSection'
import { Input } from '@/components/ui/input'
import { apiGet, useCatalog } from './entriesApi'
import { SearchableSelect } from './SearchableSelect'
import { PlatformMark } from '../shell/PlatformMark'
import { DomainCandidateDetail } from './DomainCandidateDetail'
import { CampaignDetailPage } from './CampaignDetailPage'
import { Chip, FilterPill, fmtAge } from './autoDomainChips'
import type { ProcessedTheme } from '../ProcessedEntry/DomainEntry/processedTypes'
import { parseDbTimestamp } from '@/lib/dates'
import {
  BLOCKED_REASON_LABEL,
  BLOCKED_REASON_TITLE,
  BRAND_POSITION_LABEL,
  CHIP_TONE_CLASS,
  EVASIVE_TITLE,
  isCloudflareBlock,
  VANTAGE_LABEL,
  VANTAGE_TITLE,
  VANTAGE_TONE,
  STATUS_LABEL,
  STATUS_ORDER,
  STATUS_TONE,
  VERDICT_LABEL,
  VERDICT_ORDER,
  VERDICT_TONE,
  verdictOf,
  type AutoDomainCandidateRow,
  type AutoDomainRun,
  type AutoDomainRunQueryRow,
  type RunQueryStatus,
  RUN_QUERY_STATUS_LABEL,
  runQueryStatus,
  type AutoDomainSummary,
  type BrandPosition,
  type CandidateListResponse,
  type CandidateStatus,
  type CandidateVerdict,
 aliveForDays } from './autoDomainTypes'

// Domain Candidates — every hostname the sourcing side (jupyter-fraud
// libs/tooling/domain_sourcing.py + MerkleMap, or the dummy client while that
// is disabled) proposed for a brand, with what the analysis pass found out
// about it. Deliberately OUTSIDE the entry/campaign tables: a candidate only
// becomes a report when someone presses Promote on it (see
// DomainCandidateDetail), the same way scraped calls only become entries
// once the scraper decides they qualify. Read through
// backend/routes/backoffice/autoDomains.ts (auto_domain_candidate_flat +
// get_auto_domain_summary); filtering/search/paging are all server-side —
// unlike Unified Entries this list is expected to grow into the thousands
// per brand.

const SORT_OPTIONS = [
  { value: 'quality_score', label: 'Sort: Sourcing quality' },
  { value: 'verdict_score', label: 'Sort: Verdict score' },
  { value: 'analyzed_at', label: 'Sort: Last analysed' },
  { value: 'created_at', label: 'Sort: Sourced at' },
  { value: 'run', label: 'Sort: Run (newest first)' },
  { value: 'first_seen', label: 'Sort: First seen (CT)' },
  { value: 'whois_created_at', label: 'Sort: Registration date' },
  { value: 'hostname', label: 'Sort: Hostname' },
]

// The list filters persist per dashboard instance in a cookie, the way the
// sidebar keeps its collapsed state: reopening the page lands on the same
// sort/filters instead of the defaults. Search text and the run pin are
// deliberately not persisted — they are about one look, not a preference.
type PersistedFilters = {
  statuses: CandidateStatus[]
  verdicts: CandidateVerdict[]
  strategy: string
  brandPosition: '' | BrandPosition
  hideKnown: boolean
  evasive: boolean
  sort: string
}
const FILTER_COOKIE_MAX_AGE = 60 * 60 * 24 * 365
const filterCookieName = (key: string) => `domain_candidates_filters_${key.replace(/[^a-z0-9_-]/gi, '_')}`
const readFilterCookie = (key: string): Partial<PersistedFilters> => {
  if (typeof document === 'undefined') return {}
  const prefix = `${filterCookieName(key)}=`
  const raw = document.cookie.split('; ').find((c) => c.startsWith(prefix))
  if (!raw) return {}
  try {
    const parsed = JSON.parse(decodeURIComponent(raw.slice(prefix.length)))
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}
const writeFilterCookie = (key: string, value: PersistedFilters) => {
  if (typeof document === 'undefined') return
  document.cookie = `${filterCookieName(key)}=${encodeURIComponent(JSON.stringify(value))}; path=/; max-age=${FILTER_COOKIE_MAX_AGE}; SameSite=Lax`
}

const BRAND_POSITION_OPTIONS: Array<{ value: '' | BrandPosition; label: string }> = [
  { value: '', label: 'Any brand position' },
  { value: 'sld', label: BRAND_POSITION_LABEL.sld },
  { value: 'subdomain', label: BRAND_POSITION_LABEL.subdomain },
  { value: 'both', label: BRAND_POSITION_LABEL.both },
  { value: 'none', label: BRAND_POSITION_LABEL.none },
]

function fmtDate(iso?: string | null) {
  const d = parseDbTimestamp(iso ?? undefined)
  if (!d) return '—'
  const time = new Intl.DateTimeFormat('pt-PT', { hour: '2-digit', minute: '2-digit' }).format(d)
  const date = new Intl.DateTimeFormat('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(d)
  return `${time} ${date}`
}

function fmtScore(v: number | null | undefined) {
  return v == null ? '—' : Number(v).toFixed(2)
}

export function DomainCandidatesDashboardView({
  theme,
  platformIds,
  storageKey,
  isEditor,
}: {
  theme: ProcessedTheme
  platformIds?: number[]
  storageKey: string
  isEditor?: boolean
}) {
  const { isDark, muted, border, text, panelBg } = baseDashboardClasses(theme)

  // The filter bar pins under the dashboard's sticky title bar, and the
  // table's own top bar + column headers stack under it (stickyOffset), so
  // search, filters and headers all stay visible while scrolling the list.
  // Not the table's `toolbar` slot: a filter change sets `loading`, which
  // unmounts the table and would take the search input (and its focus)
  // with it. Callback ref: the bar remounts when a detail page closes.
  // The frame reports topbar + heading (onStackTop below); read here, in
  // the view's own body, the context only holds the topbar.
  const outerTop = useStickyStackTop()
  const [stackTop, setStackTop] = useState(outerTop)
  const [stickyBarEl, setStickyBarEl] = useState<HTMLDivElement | null>(null)
  const [stickyBarHeight, setStickyBarHeight] = useState(0)
  useEffect(() => {
    if (!stickyBarEl) { setStickyBarHeight(0); return }
    const ro = new ResizeObserver(() => setStickyBarHeight(stickyBarEl.offsetHeight))
    ro.observe(stickyBarEl)
    setStickyBarHeight(stickyBarEl.offsetHeight)
    return () => ro.disconnect()
  }, [stickyBarEl])
  const { catalog } = useCatalog()

  // Interactive platform pick — only offered when this instance is not
  // already pre-scoped by a per-brand caller (renderBrandDomains). Nothing
  // picked means every platform the caller may see (2026-10-22: the
  // entries-level board searches across brands; the backend resolves the
  // empty scope to all platforms for an Editor, the tenant's own otherwise),
  // so platformIdsKey '' is a real scope here, not "nothing to load".
  const [platformFilter, setPlatformFilter] = useState<number | ''>('')
  const effectivePlatformIds = platformIds && platformIds.length > 0 ? platformIds : platformFilter !== '' ? [platformFilter] : undefined
  const platformIdsKey = effectivePlatformIds ? effectivePlatformIds.join(',') : ''
  const crossPlatform = !platformIds || platformIds.length === 0
  const platformQuery = platformIdsKey ? `platform_ids=${platformIdsKey}` : ''
  const platformName = useMemo(() => {
    if (!effectivePlatformIds || effectivePlatformIds.length !== 1) return null
    return catalog.platforms.find((p) => p.id === effectivePlatformIds[0])?.name ?? null
  }, [catalog.platforms, effectivePlatformIds])

  const [candidates, setCandidates] = useState<AutoDomainCandidateRow[]>([])
  const [total, setTotal] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(false)
  const [summary, setSummary] = useState<AutoDomainSummary | null>(null)
  const [runs, setRuns] = useState<AutoDomainRun[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [lastUpdated, setLastUpdated] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // The open candidate is the URL's last segment (/ctt/domain-candidates/{id}),
  // so a candidate can be linked, reloaded and opened in a new tab.
  const detail = useDetailPath()
  const selectedId = detail.id
  // Drill-through from a known candidate to the campaign its domain already
  // belongs to — the same CampaignDetailPage Unified Entries opens, so a
  // re-sighting reads as "part of this existing campaign", never as a new thing.
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null)
  // Coming back from a candidate: same scroll position, the row highlighted.
  const back = useListReturn()

  const persisted = useMemo(() => readFilterCookie(storageKey), [storageKey])
  // One pick per row: a candidate has one status and one verdict, so ticking
  // two in a row could only widen the list (OR) — which read as narrowing. A
  // cookie from before keeps just its first pick.
  const [statuses, setStatuses] = useState<CandidateStatus[]>((persisted.statuses ?? []).slice(0, 1))
  const [verdicts, setVerdicts] = useState<CandidateVerdict[]>((persisted.verdicts ?? []).slice(0, 1))
  const [strategy, setStrategy] = useState(persisted.strategy ?? '')
  const [brandPosition, setBrandPosition] = useState<'' | BrandPosition>(persisted.brandPosition ?? '')
  // Known = the hostname already exists in `domain` (came in via a report
  // earlier). Shown by default: a re-sighting of an old lure is information,
  // and the dummy seeds are all known.
  const [hideKnown, setHideKnown] = useState(persisted.hideKnown ?? false)
  // Only the rows the evasion rule flagged: a Cloudflare challenge on a fresh,
  // lure-named domain (autoDomainTypes.EVASIVE_TITLE). Off by default.
  const [evasive, setEvasive] = useState(persisted.evasive ?? false)
  const [runId, setRunId] = useState('')
  // What the selected run searched, pattern by pattern (the "Searched"
  // section). Empty unless a run is picked.
  const [runQueries, setRunQueries] = useState<AutoDomainRunQueryRow[]>([])
  const [runQueryFilter, setRunQueryFilter] = useState<'' | RunQueryStatus>('')
  const [sort, setSort] = useState(persisted.sort ?? 'quality_score')
  useEffect(() => {
    writeFilterCookie(storageKey, { statuses, verdicts, strategy, brandPosition, hideKnown, evasive, sort })
  }, [storageKey, statuses, verdicts, strategy, brandPosition, hideKnown, evasive, sort])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [query])

  const buildParams = useCallback((targetPage: number) => {
    const params = new URLSearchParams({ page: String(targetPage), sort, dir: sort === 'hostname' ? 'asc' : 'desc' })
    if (platformIdsKey) params.set('platform_ids', platformIdsKey)
    if (statuses.length) params.set('status', statuses.join(','))
    if (verdicts.length) params.set('verdict', verdicts.join(','))
    if (strategy) params.set('strategy', strategy)
    if (brandPosition) params.set('brand_position', brandPosition)
    if (hideKnown) params.set('hide_known', '1')
    if (evasive) params.set('evasive', '1')
    if (runId) params.set('run_id', runId)
    if (debouncedQuery) params.set('q', debouncedQuery)
    return params
  }, [platformIdsKey, sort, statuses, verdicts, strategy, brandPosition, hideKnown, evasive, runId, debouncedQuery])

  const fetchSeq = useRef(0)
  const fetchCandidates = useCallback(async (targetPage = 0, isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    else if (targetPage === 0) setLoading(true)
    else setLoadingMore(true)
    setError(null)
    // Only the newest request may write the list. The unfiltered query is the
    // slow one, so without this its answer lands after a filtered one and the
    // table shows every verdict under a chip that says "Brand impersonation".
    const seq = ++fetchSeq.current
    const result = await apiGet<CandidateListResponse>(`/auto-domains/candidates?${buildParams(targetPage).toString()}`)
    if (seq !== fetchSeq.current) return
    if (result.success && result.data) {
      setCandidates((prev) => (targetPage === 0 ? result.data!.candidates : [...prev, ...result.data!.candidates]))
      setTotal(result.data.total)
      setHasMore(result.data.hasMore)
      setPage(targetPage)
      setLastUpdated(Date.now())
    } else {
      setError(result.error ?? 'Failed to load candidates')
    }
    setLoading(false)
    setRefreshing(false)
    setLoadingMore(false)
  }, [platformIdsKey, buildParams])

  const fetchSummary = useCallback(async () => {
    const summaryParams = new URLSearchParams()
    if (platformIdsKey) summaryParams.set('platform_ids', platformIdsKey)
    if (runId) summaryParams.set('run_id', runId)
    const [s, r] = await Promise.all([
      apiGet<AutoDomainSummary>(`/auto-domains/summary?${summaryParams.toString()}`),
      apiGet<AutoDomainRun[]>(`/auto-domains/runs${platformQuery ? `?${platformQuery}` : ''}`),
    ])
    if (s.success && s.data) setSummary(s.data)
    if (r.success && r.data) setRuns(r.data)
  }, [platformIdsKey, platformQuery, runId])

  useEffect(() => { fetchCandidates(0) }, [fetchCandidates])
  useEffect(() => { fetchSummary() }, [fetchSummary])
  useEffect(() => {
    if (!runId) { setRunQueries([]); setRunQueryFilter(''); return }
    let live = true
    apiGet<AutoDomainRunQueryRow[]>(`/auto-domains/runs/${encodeURIComponent(runId)}/queries`)
      .then((r) => { if (live) setRunQueries(r.success && r.data ? r.data : []) })
    return () => { live = false }
  }, [runId])

  const refreshAll = useCallback(() => { fetchCandidates(0, true); fetchSummary() }, [fetchCandidates, fetchSummary])

  const toggleStatus = (s: CandidateStatus) => setStatuses((prev) => (prev.includes(s) ? [] : [s]))
  const toggleVerdict = (v: CandidateVerdict) => setVerdicts((prev) => (prev.includes(v) ? [] : [v]))

  const columns: ColumnDef[] = useMemo(() => [
    // Across brands the platform is the first thing to read on a row; a
    // per-brand board already says it in its title.
    ...(crossPlatform ? [{ key: 'platform', label: 'Platform', initialWidth: 110 }] : []),
    { key: 'hostname', label: 'Hostname', initialWidth: 300 },
    { key: 'status', label: 'Status', initialWidth: 100 },
    { key: 'verdict', label: 'Verdict', initialWidth: 150 },
    { key: 'quality', label: 'Quality', initialWidth: 70, thTitle: 'Sourcing quality: 0.40·term_freq + 0.30·size + 0.15·corroboration + 0.15·lure position' },
    { key: 'provenance', label: 'Strategy / Gram', initialWidth: 220 },
    { key: 'corroboration', label: '#Grams', initialWidth: 60, thTitle: 'How many grams independently reached this hostname' },
    { key: 'tld', label: 'TLD', initialWidth: 70 },
    { key: 'known', label: 'Already flagged', initialWidth: 130, thTitle: 'The hostname is already in the domain table — this is a re-sighting of a flagged domain, with the number of entries that carry it' },
    { key: 'registered', label: 'Registered', initialWidth: 120, thTitle: 'WHOIS creation date (age) and registrar; "unregistered" = WHOIS has no record for the name' },
    { key: 'first_seen', label: 'First seen (CT)', initialWidth: 130, thTitle: "MerkleMap's first observation of a certificate for this hostname" },
    { key: 'analyzed_at', label: 'Analysed', initialWidth: 140 },
    { key: 'created_at', label: 'Sourced', initialWidth: 140 },
    { key: 'run', label: 'Run', initialWidth: 150, thTitle: 'The sourcing run that first added this host (started at · start kind). Click to filter the list to that run.' },
  ], [crossPlatform])

  const runQueryColumns: ColumnDef[] = useMemo(() => [
    { key: 'n', label: '#', initialWidth: 50, thTitle: 'Position in the request the browser session received' },
    { key: 'strategy', label: 'Strategy', initialWidth: 170 },
    { key: 'pattern', label: 'Pattern', initialWidth: 260, thTitle: 'The MerkleMap query exactly as sent' },
    { key: 'type', label: 'Type', initialWidth: 80 },
    { key: 'rows', label: 'Rows', initialWidth: 60, thTitle: 'Hostnames the search returned (one page of ~20)' },
    { key: 'total', label: 'Total', initialWidth: 90, thTitle: "MerkleMap's own count of matching hosts, when it showed one" },
    { key: 'candidates', label: 'Candidates', initialWidth: 110, thTitle: 'Rows that survived filtering into candidates, and how many were new' },
    { key: 'executed_at', label: 'Searched', initialWidth: 130 },
    { key: 'status', label: 'Status', initialWidth: 110 },
  ], [])

  const strategyOptions = useMemo(() => Object.keys(summary?.strategy ?? {}).sort(), [summary])
  const verdictStrategyMatrix = useMemo(() => {
    const rows = summary?.verdict_strategy ?? []
    const strategies = Array.from(new Set(rows.map((r) => r.strategy))).sort()
    const cell = new Map<string, number>()
    for (const r of rows) cell.set(`${r.strategy}|${r.verdict}`, r.candidates)
    const activeVerdicts = VERDICT_ORDER.filter((v) => rows.some((r) => r.verdict === v))
    return { strategies, verdicts: activeVerdicts, cell }
  }, [summary])

  if (selectedCampaignId) {
    return (
      <CampaignDetailPage
        campaignId={selectedCampaignId}
        theme={theme}
        storageKey={storageKey}
        isEditor={isEditor}
        onClose={() => setSelectedCampaignId(null)}
      />
    )
  }

  if (selectedId) {
    return (
      <DomainCandidateDetail
        candidateId={selectedId}
        theme={theme}
        isEditor={isEditor}
        onClose={detail.close}
        onChanged={refreshAll}
        onOpenCampaign={(campaignId) => { detail.close(); setSelectedCampaignId(campaignId) }}
      />
    )
  }

  const tile = (label: string, value: number | undefined, tone: 'red' | 'amber' | 'sky' | 'green') => (
    <div className={`col-span-1 overflow-hidden border ${border}`}>
      <StatTile label={label} value={value == null ? '—' : String(value)} tone={tone} isDark={isDark} />
    </div>
  )
  const st = summary?.status ?? {}
  const vd = summary?.verdict ?? {}
  const pending = (st.sourced?.candidates ?? 0) + (st.analyzing?.candidates ?? 0)
  const live = Object.values(vd).reduce((n, c) => n + c.live, 0)
  const newTotal = Object.values(st).reduce((n, c) => n + c.new_candidates, 0)
  const alreadyFlagged = summary ? summary.total - newTotal : undefined
  // The brand's jobs card reads the Editor-only /jobs endpoints and is about
  // one platform, so it only appears on a single-brand page for an Editor.
  const jobsCard = !!isEditor && effectivePlatformIds?.length === 1

  return (
    <BaseDashboard
      rootRef={back.restoreRef}
      theme={theme}
      eyebrow={platformName ? `${platformName} · Domain Candidates` : 'Moderation · Domain Candidates'}
      title="Domain Candidates"
      subtitle={`Hostnames sourced for ${crossPlatform && !platformIdsKey ? 'every brand' : 'this brand'} and what the analysis found — nothing here is an entry until it is promoted.`}
      infoContent={
        <>
          <p><span className="font-semibold">auto_domain_candidate_flat</span> — one row per (sourcing run, hostname): which strategy/gram found it, its sourcing quality, and the latest analysis verdict. Filters, search and paging run server-side.</p>
          <p className="mt-1.5">Promote creates a raw entry through the normal pipeline (source <span className="font-mono">auto_domains</span>); Discard keeps the row with a reason. Both are Python-owned actions the backoffice only forwards.</p>
        </>
      }
      onRefresh={refreshAll}
      refreshing={refreshing}
      lastUpdated={lastUpdated}
      storageKey={`domain-candidates-${storageKey}-header`}
      widgets={summary ? (
        <>
          {tile('Total', summary.total, 'sky')}
          {tile('Already flagged', alreadyFlagged, 'green')}
          {tile('Unflagged', newTotal, 'amber')}
          {tile('Pending', pending, 'amber')}
          {tile('Analysed', st.analyzed?.candidates, 'sky')}
          {tile('Live', live, 'green')}
          {tile('Impersonation', vd.brand_lure?.candidates ?? 0, 'red')}
          {tile('Evasive', summary.evasive?.true?.candidates ?? 0, 'red')}
          {tile('Promoted', st.promoted?.candidates ?? 0, 'green')}
          {tile('Discarded', st.discarded?.candidates ?? 0, 'amber')}
          <RegistrationWaveCard summary={summary} isDark={isDark} muted={muted} text={text} className={`${jobsCard ? 'col-span-5' : 'col-span-6'} row-start-2 h-full`} />
          <QualityDistributionCard summary={summary} isDark={isDark} muted={muted} text={text} className={`${jobsCard ? 'col-span-3' : 'col-span-4'} row-start-2 h-full`} />
          {jobsCard && <BrandJobsCard platformId={effectivePlatformIds![0]} platformName={platformName} isDark={isDark} muted={muted} text={text} className="col-span-2 row-start-2 h-full" />}
          <ScreenFunnelCard platformIdsKey={platformIdsKey} refreshToken={lastUpdated} isDark={isDark} muted={muted} text={text} className="col-span-10 row-start-3 h-full" />
        </>
      ) : undefined}
      widgetCols={10}
      widgetRows="5.25rem 14rem 14rem"
      sections
      onStackTop={setStackTop}
    >
      {!platformIds && (
        <DashboardSection theme={theme} title="Platform">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-56">
            <SearchableSelect theme={theme} value={platformFilter === '' ? undefined : platformFilter}
              options={catalog.platforms} placeholder="All platforms" searchPlaceholder="Search platforms…" noneLabel="All platforms"
              onChange={(id) => { setPlatformFilter(id ? Number(id) : ''); setRunId('') }} />
          </div>
          {!platformIdsKey && <span className={`font-mono text-[11px] ${muted}`}>Every brand's candidates, searched together — pick a platform to narrow.</span>}
        </div>
        </DashboardSection>
      )}

      {summary && (
        <CollapsibleSection label="Signals — what the sourcing is actually finding" theme={theme}>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2 overflow-x-auto">
              <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${muted}`}>Verdict × strategy (click a cell to filter)</div>
              <table className="w-full text-[11px] font-mono">
                <thead>
                  <tr>
                    <th className={`px-2 py-1 text-left ${muted}`}>strategy</th>
                    {verdictStrategyMatrix.verdicts.map((v) => (
                      <th key={v} className={`px-2 py-1 text-right ${CHIP_TONE_CLASS[VERDICT_TONE[v]].split(' ').pop()}`}>{VERDICT_LABEL[v]}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {verdictStrategyMatrix.strategies.map((s) => (
                    <tr key={s} className={`border-t ${border}`}>
                      <td className={`px-2 py-1 ${text}`}>{s}</td>
                      {verdictStrategyMatrix.verdicts.map((v) => {
                        const n = verdictStrategyMatrix.cell.get(`${s}|${v}`) ?? 0
                        return (
                          <td key={v} className="px-2 py-1 text-right">
                            {n > 0 ? (
                              <button type="button" onClick={() => { setStrategy(s); setVerdicts([v]) }} className="hover:text-signal underline-offset-2 hover:underline">{n}</button>
                            ) : <span className={muted}>·</span>}
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3">
              <div>
                <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${muted}`}>Brand position</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(summary.brand_position).map(([k, c]) => (
                    <FilterPill key={k} active={brandPosition === k} label={`${BRAND_POSITION_LABEL[k as BrandPosition] ?? k}`} count={c.candidates} tone="sky" border={border} muted={muted}
                      onClick={() => setBrandPosition((prev) => (prev === k ? '' : (k as BrandPosition)))} />
                  ))}
                </div>
              </div>
              <div>
                <div className={`mb-1.5 font-mono text-[9px] uppercase tracking-widest ${muted}`}>Top TLDs</div>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(summary.tld).sort((a, b) => b[1].candidates - a[1].candidates).slice(0, 10).map(([k, c]) => (
                    <FilterPill key={k} active={debouncedQuery === `.${k}`} label={`.${k}`} count={c.candidates} tone="neutral" border={border} muted={muted}
                      onClick={() => setQuery((prev) => (prev === `.${k}` ? '' : `.${k}`))} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}

      {/* The candidates themselves: the filter bar, the patterns of a
          selected run, and the table, one card. The vocabulary has its own
          page (Administration › Brand Vocabulary); from here it is one click
          away with this platform preselected. */}
      <DashboardSection
        theme={theme}
        title={`Candidates${total != null ? ` · ${total.toLocaleString('en')}` : ''}`}
        padded={false}
        actions={isEditor && effectivePlatformIds?.length === 1 ? (
          <a href={`/entries/brand-vocabulary?platform_id=${effectivePlatformIds[0]}`} className={`font-mono text-[10px] uppercase tracking-widest ${muted} hover:text-signal`}>
            Sourcing vocabulary for {platformName ?? 'this brand'} →
          </a>
        ) : undefined}
      >
      <div ref={setStickyBarEl} className={`sticky z-30 px-4 py-3 border-b flex flex-col gap-2 ${border} ${panelBg}`} style={{ top: stackTop }}>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className={`pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${muted}`} />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search hostname, registrable domain or gram…"
              className={`h-7 pl-8 font-mono text-[11px] ${''}`}
            />
          </div>
          <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className={`h-7 rounded border bg-transparent px-2 font-mono text-[11px] ${border} ${text}`}>
            <option value="">All strategies</option>
            {strategyOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={brandPosition} onChange={(e) => setBrandPosition(e.target.value as '' | BrandPosition)} className={`h-7 rounded border bg-transparent px-2 font-mono text-[11px] ${border} ${text}`}>
            {BRAND_POSITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={runId} onChange={(e) => setRunId(e.target.value)} className={`h-7 rounded border bg-transparent px-2 font-mono text-[11px] ${border} ${text}`}>
            <option value="">All runs</option>
            {runs.map((r) => (
              <option key={r.id} value={r.id}>{fmtDate(r.started_at)} · {r.start_kind} · {r.triggered_by ?? '—'}</option>
            ))}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className={`h-7 rounded border bg-transparent px-2 font-mono text-[11px] ${border} ${text}`}>
            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            type="button"
            onClick={() => setHideKnown((v) => !v)}
            className={`rounded-[4px] border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              hideKnown ? 'border-signal bg-signal-surface text-signal' : `${border} ${muted} hover:text-signal hover:border-signal`
            }`}
            title="Show only hostnames the backoffice does not have yet — hide re-sightings of already-flagged domains"
          >
            Hide flagged
          </button>
          <button
            type="button"
            onClick={() => setEvasive((v) => !v)}
            className={`rounded-[4px] border px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${
              evasive ? 'border-destructive bg-danger-surface text-destructive' : `${border} ${muted} hover:text-destructive hover:border-destructive`
            }`}
            title={EVASIVE_TITLE}
          >
            Evasive (Cloudflare){summary?.evasive?.true?.candidates ? ` · ${summary.evasive.true.candidates}` : ''}
          </button>
          <span className={`font-mono text-[10px] uppercase tracking-widest whitespace-nowrap ${muted}`}>
            {candidates.length} / {total ?? '—'}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`font-mono text-[9px] uppercase tracking-widest mr-1 ${muted}`}>Status</span>
          {STATUS_ORDER.map((s) => (
            <FilterPill key={s} active={statuses.includes(s)} label={STATUS_LABEL[s]} count={st[s]?.candidates} tone={STATUS_TONE[s]} border={border} muted={muted} onClick={() => toggleStatus(s)} />
          ))}
          <span className={`font-mono text-[9px] uppercase tracking-widest ml-3 mr-1 ${muted}`}>Verdict</span>
          {VERDICT_ORDER.map((v) => (
            <FilterPill key={v} active={verdicts.includes(v)} label={VERDICT_LABEL[v]} count={vd[v]?.candidates} tone={VERDICT_TONE[v]} border={border} muted={muted} onClick={() => toggleVerdict(v)} />
          ))}
          {(statuses.length > 0 || verdicts.length > 0 || strategy || brandPosition || query || runId || evasive) && (
            <button type="button" onClick={() => { setStatuses([]); setVerdicts([]); setStrategy(''); setBrandPosition(''); setQuery(''); setRunId(''); setEvasive(false) }} className={`ml-2 font-mono text-[10px] uppercase tracking-widest hover:text-signal ${muted}`}>
              clear
            </button>
          )}
        </div>
      </div>

      {/* What the selected run searched, pattern by pattern. Until 2026-09-25
          nothing showed this, and the browser session had been handing back
          only the first half of every request for days; "not fetched" is that
          gap made visible. Rows are in the order the request was sent. */}
      {runId && (() => {
        const counts = runQueries.reduce((m, r) => { m[runQueryStatus(r)] += 1; return m },
          { answered: 0, zero: 0, not_fetched: 0, not_run: 0 } as Record<RunQueryStatus, number>)
        const rowsTotal = runQueries.reduce((n, r) => n + r.result_count, 0)
        const shown = runQueryFilter ? runQueries.filter((r) => runQueryStatus(r) === runQueryFilter) : runQueries
        const tone: Record<RunQueryStatus, 'emerald' | 'neutral' | 'red' | 'amber'> = { answered: 'emerald', zero: 'neutral', not_fetched: 'red', not_run: 'amber' }
        return (
          <div className={`border-b px-4 py-3 ${border}`}>
          <CollapsibleSection label={`Searched — ${runQueries.length} patterns in this run`} theme={theme}>
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              {(Object.keys(tone) as RunQueryStatus[]).map((k) => (
                <FilterPill key={k} active={runQueryFilter === k} label={RUN_QUERY_STATUS_LABEL[k]} count={counts[k]} tone={tone[k]} border={border} muted={muted}
                  onClick={() => setRunQueryFilter((prev) => (prev === k ? '' : k))} />
              ))}
              <span className={`ml-2 font-mono text-[10px] uppercase tracking-widest ${muted}`}>{rowsTotal} rows returned</span>
              {counts.not_fetched > 0 && (
                <span className={`font-mono text-[10px] text-destructive`} title="The browser session finished but its answer did not include these patterns; they are recorded as failed searches, never as patterns that found nothing">
                  — {counts.not_fetched} searched but never handed back
                </span>
              )}
            </div>
            <ResizableTable
              columns={runQueryColumns}
              stickyHeader
              maxRows={10}
              maximizable={false}
              theme={theme}
              isEmpty={shown.length === 0}
              emptyMessage={runQueries.length === 0 ? 'No patterns recorded for this run' : 'No patterns with this status'}
            >
              {shown.map((r) => {
                const s = runQueryStatus(r)
                const n = runQueries.indexOf(r) + 1
                return (
                  <tr key={r.gram_id} className={`border-b border-border ${s === 'not_fetched' ? 'bg-danger-surface' : ''}`}>
                    <TableTd className={`font-mono ${muted}`}>{n}</TableTd>
                    <TableTd className={`font-mono ${muted}`}>{r.strategy_code ?? '—'}</TableTd>
                    <TableTd className={`font-mono ${text}`} title={r.gram !== r.query_string ? `gram ${r.gram}` : undefined}>{r.query_string}</TableTd>
                    <TableTd className={`font-mono ${muted}`}>{r.query_type}</TableTd>
                    <TableTd className={`font-mono text-right ${text}`}>{r.executed ? r.result_count : '—'}</TableTd>
                    <TableTd className={`font-mono text-right ${muted}`}>{r.total_count != null ? r.total_count.toLocaleString('en') : '—'}</TableTd>
                    <TableTd className={`font-mono text-right ${text}`}>
                      {r.candidate_count}
                      {r.new_candidate_count > 0 && <span className={`ml-1 ${muted}`}>+{r.new_candidate_count} new</span>}
                    </TableTd>
                    <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(r.executed_at)}</TableTd>
                    <TableTd><Chip label={RUN_QUERY_STATUS_LABEL[s]} tone={tone[s]} title={r.error ?? undefined} /></TableTd>
                  </tr>
                )
              })}
            </ResizableTable>
          </CollapsibleSection>
          </div>
        )
      })()}

      {loading ? (
        <div className="flex h-48 items-center justify-center">
          <span className={`font-mono text-[11px] uppercase tracking-widest animate-pulse ${muted}`}>Loading…</span>
        </div>
      ) : error ? (
        <div className={`flex h-48 items-center justify-center gap-3 text-destructive`}>
          <AlertCircle className="h-5 w-5" />
          <span className="font-mono text-[13px]">{error}</span>
        </div>
      ) : (
        <>
          <ResizableTable
            columns={columns}
            stickyHeader
            stickyOffset={stickyBarHeight}
            theme={theme}
            isEmpty={candidates.length === 0}
            emptyMessage="No candidates match these filters"
          >
            {candidates.map((c) => {
              const v = verdictOf(c)
              const lure = v === 'brand_lure'
              return (
                <tr
                  key={c.candidate_id}
                  data-candidate-id={c.candidate_id}
                  data-last-opened={back.isLast(c.candidate_id) || undefined}
                  onClick={(ev) => {
                    // Ctrl/Cmd-click anywhere on the row: the candidate in a
                    // new tab, the list stays (the hostname is a real link
                    // too, for the right-click menu and middle-click)
                    if (ev.ctrlKey || ev.metaKey) { window.open(detail.href(c.candidate_id), '_blank', 'noopener'); return }
                    back.open(c.candidate_id, ev.currentTarget, () => detail.open(c.candidate_id))
                  }}
                  onAuxClick={(ev) => { if (ev.button === 1) window.open(detail.href(c.candidate_id), '_blank', 'noopener') }}
                  className={`cursor-pointer border-b border-l-2 transition-colors border-border ${
                    lure || c.evasive ? 'border-l-destructive bg-danger-surface hover:bg-danger-surface/70' : c.is_alive ? 'border-l-signal hover:bg-secondary' : 'border-l-transparent hover:bg-secondary'
                  } ${back.isLast(c.candidate_id) ? LAST_OPENED_ROW_CLASS : ''}`}
                >
                  {crossPlatform && (
                    <TableTd className={`font-mono ${muted}`}>
                      {(() => {
                        const p = catalog.platforms.find((x) => x.id === c.platform_id)
                        const code = p?.name_code ?? c.platform_name_code ?? (c.platform_id != null ? `#${c.platform_id}` : '—')
                        return <span className="inline-flex items-center gap-1.5"><PlatformMark code={code} label={p?.name ?? c.platform_name ?? code} logoUrl={p?.logo_url} />{code}</span>
                      })()}
                    </TableTd>
                  )}
                  <TableTd className="font-mono">
                    <div className="flex items-center gap-2 min-w-0">
                      {/* A real link to the candidate's page (/{brand}/domain-candidates/{id}):
                          right-click "open in new tab", middle-click and Ctrl/Cmd-click are
                          the browser's own; a plain click stays in the app through the row. */}
                      <a href={detail.href(c.candidate_id)} data-action="candidate-link" className={`truncate ${text} hover:underline`} title={c.hostname}
                        onClick={(ev) => {
                          if (ev.ctrlKey || ev.metaKey || ev.shiftKey || ev.altKey || ev.button !== 0) { ev.stopPropagation(); return }
                          ev.preventDefault()   // the row's handler navigates in place and remembers the scroll
                        }}
                        onAuxClick={(ev) => ev.stopPropagation()}>{c.hostname}</a>
                      {aliveForDays(c) != null && <Chip label={`alive ${aliveForDays(c)} d`} tone="red" title={`Up since it was first sighted, or registered if that is later (a re-registered domain can be sighted before its current registration); the latest probe (${fmtDate(c.last_probe_at ?? c.analyzed_at)}) still saw it live`} />}
                      {c.evasive && <Chip label="evasive" tone="red" title={EVASIVE_TITLE} />}
                      {c.brand_position !== 'none' && c.brand_position !== 'sld' && (
                        <Chip label={c.brand_position} tone="amber" title={BRAND_POSITION_LABEL[c.brand_position]} />
                      )}
                    </div>
                  </TableTd>
                  <TableTd><Chip label={STATUS_LABEL[c.status]} tone={STATUS_TONE[c.status]} title={c.discarded_reason ?? c.filtered_reason ?? undefined} /></TableTd>
                  <TableTd>
                    <div className="flex items-center gap-1.5">
                      <Chip label={VERDICT_LABEL[v]} tone={VERDICT_TONE[v]} />
                      {c.blocked_reason && <Chip label={BLOCKED_REASON_LABEL[c.blocked_reason] ?? c.blocked_reason} tone={isCloudflareBlock(c.blocked_reason) ? 'amber' : 'neutral'} title={BLOCKED_REASON_TITLE[c.blocked_reason] ?? 'What the latest probe found in front of the host'} />}
                      {c.verdict_vantage && <Chip label={VANTAGE_LABEL[c.verdict_vantage]} tone={VANTAGE_TONE[c.verdict_vantage]} title={VANTAGE_TITLE[c.verdict_vantage]} />}
                      {c.last_probe_verdict && c.last_probe_verdict !== c.verdict && (
                        <Chip label={`now: ${VERDICT_LABEL[c.last_probe_verdict]}`} tone="neutral" title={`The latest probe (${c.last_probe_vantage ? VANTAGE_LABEL[c.last_probe_vantage] : '?'}, ${fmtDate(c.last_probe_at)}) saw ${VERDICT_LABEL[c.last_probe_verdict]}; the verdict is the best any pass has seen`} />
                      )}
                      {c.verdict_score != null && <span className={`font-mono text-[10px] ${muted}`}>{fmtScore(c.verdict_score)}</span>}
                    </div>
                  </TableTd>
                  <TableTd className={`font-mono ${muted}`}>{fmtScore(c.quality_score)}</TableTd>
                  <TableTd className="font-mono">
                    <div className="min-w-0">
                      <div className={`truncate text-[11px] ${text}`}>{c.strategy_code ?? '—'}</div>
                      <div className={`truncate text-[10px] ${muted}`} title={c.query_string ?? undefined}>{c.gram ?? '—'}</div>
                    </div>
                  </TableTd>
                  <TableTd className={`font-mono ${muted}`}>{c.corroboration_count}</TableTd>
                  <TableTd className={`font-mono ${muted}`}>{c.tld ?? '—'}</TableTd>
                  <TableTd>
                    {c.is_known ? (
                      <span className="inline-flex items-center gap-1" title={c.known_last_entry_at ? `last report ${fmtDate(c.known_last_entry_at)}` : undefined}>
                        <Link2 className="h-3 w-3 text-success" aria-hidden />
                        <Chip label={`flagged · ${c.known_entry_count} ${c.known_entry_count === 1 ? 'entry' : 'entries'}`} tone="emerald" />
                      </span>
                    ) : <Chip label="new" tone="amber" />}
                  </TableTd>
                  <TableTd className="font-mono whitespace-nowrap">
                    {c.whois_registered === false ? (
                      <Chip label="unregistered" tone="amber" />
                    ) : c.whois_created_at ? (
                      <span title={`${fmtDate(c.whois_created_at)}${c.whois_registrar ? ` · ${c.whois_registrar}` : ''}${c.whois_expires_at ? ` · expires ${fmtDate(c.whois_expires_at)}` : ''}`} className="inline-flex items-center gap-1.5">
                        <span className={(() => { const d = parseDbTimestamp(c.whois_created_at ?? undefined); const days = d ? (Date.now() - d.getTime()) / 86400000 : 999; return days <= 30 ? 'text-destructive' : days <= 180 ? 'text-warning' : text })()}>{fmtAge(c.whois_created_at)}</span>
                        {c.whois_registrar && <span className={`truncate max-w-[70px] text-[10px] ${muted}`}>{c.whois_registrar}</span>}
                      </span>
                    ) : c.whois_checked_at ? (
                      <span className={muted} title="WHOIS answered but no creation date could be parsed">?</span>
                    ) : <span className={muted}>—</span>}
                  </TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(c.first_seen)}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(c.analyzed_at)}</TableTd>
                  <TableTd className={`font-mono whitespace-nowrap ${muted}`}>{fmtDate(c.created_at)}</TableTd>
                  <TableTd className="font-mono whitespace-nowrap">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setRunId(c.run_id === runId ? '' : c.run_id) }}
                      className={`inline-flex items-center gap-1.5 hover:text-signal ${c.run_id === runId ? 'text-signal' : muted}`}
                      title={c.run_id === runId ? 'Filtered to this run — click to clear' : 'Filter to this run'}
                    >
                      <span>{fmtDate(c.run_started_at)}</span>
                      <span className="text-[9px] uppercase tracking-widest">{c.run_start_kind}</span>
                    </button>
                  </TableTd>
                </tr>
              )
            })}
          </ResizableTable>
          {hasMore && (
            <div className={`flex justify-center border-t px-4 py-3 ${border}`}>
              <button
                type="button"
                onClick={() => fetchCandidates(page + 1)}
                disabled={loadingMore}
                className={`rounded border px-4 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors disabled:opacity-50 hover:text-signal hover:border-signal ${border} ${muted}`}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
      </DashboardSection>
    </BaseDashboard>
  )
}
